import React from 'react';
import './PrintableOrderReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getOrderReport
interface OrderStructureItem {
  id: number;
  code: string;
  type: string;
  location: string;
  startDate: Date | string;
  endDate: Date | string;
  price: number;
  discount: number;
  total: number;
}

interface CreatedByUser {
  id: number;
  name: string;
}

interface OrderReportItem {
  id: number;
  number: string;
  client: {
    id: number;
    name: string;
    director?: string | null;
  };
  status: string;
  totalAmount: number;
  paidAmount: number;
  createdAt: Date | string;
  structures: OrderStructureItem[];
  createdBy?: CreatedByUser;
}

interface OrderReportData {
  orders: OrderReportItem[];
  statusBreakdown: Array<{ status: string; count: number; amount: number; percentage: number }>;
  totalRevenue: number;
  totalPaid: number;
  totalUnpaid: number;
  startDate: string;
  endDate: string;
}

interface PrintableOrderReportProps {
  reportData: OrderReportData;
  sortBy?: string;
  sortOrder?: string;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper for status text (can be expanded)
const getStatusText = (status: string): string => {
  const statuses: { [key: string]: string } = {
    reserved: 'Забронирован',
    waitingPayment: 'Ожидает оплаты',
    partiallyPaid: 'Частично оплачен',
    fullyPaid: 'Полностью оплачен',
    inProgress: 'В работе',
    completed: 'Завершен',
    cancelled: 'Отменен',
  };
  return statuses[status] || status;
};

const getSortText = (sortBy?: string, sortOrder?: string): string => {
  if (!sortBy) return '';
  
  const sortColumns: { [key: string]: string } = {
    'number': 'Номер заказа',
    'client.name': 'Клиент',
    'status': 'Статус',
    'totalAmount': 'Сумма',
    'paidAmount': 'Оплачено',
    'createdAt': 'Дата заказа',
    'createdBy': 'Кем создан'
  };
  
  const orderText = sortOrder === 'asc' ? 'по возрастанию' : 'по убыванию';
  return `Сортировка: ${sortColumns[sortBy] || sortBy} (${orderText})`;
};

const groupOrdersByCreator = (orders: OrderReportItem[]) => {
  const grouped: { [key: string]: { creator: CreatedByUser, orders: OrderReportItem[], totalAmount: number, paidAmount: number, balance: number } } = {};
  
  orders.forEach(order => {
    const creatorKey = order.createdBy?.id || 'unknown';
    const creatorName = order.createdBy?.name || 'Не указан';
    
    if (!grouped[creatorKey]) {
      grouped[creatorKey] = {
        creator: order.createdBy || { id: 0, name: 'Не указан' },
        orders: [],
        totalAmount: 0,
        paidAmount: 0,
        balance: 0
      };
    }
    
    grouped[creatorKey].orders.push(order);
    grouped[creatorKey].totalAmount += order.totalAmount;
    grouped[creatorKey].paidAmount += order.paidAmount;
    grouped[creatorKey].balance += (order.totalAmount - order.paidAmount);
  });
  
  return Object.values(grouped);
};

const PrintableOrderReport: React.FC<PrintableOrderReportProps> = ({ 
  reportData, 
  sortBy, 
  sortOrder 
}) => {
  if (!reportData) {
    return <div className="printable-container">Ошибка: Данные для отчета по заказам не найдены.</div>;
  }

  const {
    orders,
    totalRevenue,
    totalPaid,
    totalUnpaid,
    startDate,
    endDate,
  } = reportData;

  const sortInfo = getSortText(sortBy, sortOrder);
  
  const groupedByCreator = groupOrdersByCreator(orders);

  return (
    <div className="printable-container order-report-container">
      <header className="printable-header">
        <h1>Отчет по заказам</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
        {sortInfo && <p className="sort-info">{sortInfo}</p>}
      </header>

      <section className="summary-section">
        <h2>Сводка по периоду</h2>
        <div className="summary-grid">
          <div><strong>Всего заказов:</strong> {orders.length}</div>
          <div><strong>Общая сумма:</strong> {formatCurrency(totalRevenue)}</div>
          <div><strong>Оплачено:</strong> {formatCurrency(totalPaid)}</div>
          <div><strong>Неоплачено:</strong> {formatCurrency(totalUnpaid)}</div>
        </div>
      </section>

      <section className="details-table">
        <h2>Детализация по заказам</h2>
        <table>
          <thead>
            <tr>
              <th>Номер</th>
              <th>Клиент</th>
              <th>Дата</th>
              <th>Менеджер</th>
              <th>Статус</th>
              <th>Сумма (₸)</th>
              <th>Оплачено (₸)</th>
              <th>Остаток (₸)</th>
            </tr>
          </thead>
          <tbody>
            {groupedByCreator.map((group, groupIndex) => (
              <React.Fragment key={group.creator.id || `unknown-${groupIndex}`}>
                {group.orders.map((order) => (
                  <tr key={order.id} className="order-row">
                    <td>{order.number}</td>
                    <td>{order.client.name}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{order.createdBy?.name || 'Не указан'}</td>
                    <td>{getStatusText(order.status)}</td>
                    <td className="numeric">{formatCurrency(order.totalAmount)}</td>
                    <td className="numeric">{formatCurrency(order.paidAmount)}</td>
                    <td className="numeric">{formatCurrency(order.totalAmount - order.paidAmount)}</td>
                  </tr>
                ))}
                
                <tr key={`summary-${group.creator.id || groupIndex}`} className="creator-summary-row">
                  <td colSpan={5} className="summary-label">
                    Итого по менеджеру: <strong>{group.creator.name}</strong>
                  </td>
                  <td className="numeric summary-total">
                    <strong>{formatCurrency(group.totalAmount)}</strong>
                  </td>
                  <td className="numeric summary-total">
                    <strong>{formatCurrency(group.paidAmount)}</strong>
                  </td>
                  <td className="numeric summary-total">
                    <strong>{formatCurrency(group.balance)}</strong>
                  </td>
                </tr>
                
                <tr key={`spacer-${group.creator.id || groupIndex}`} className="spacer-row">
                  <td colSpan={8} style={{ height: '10px', background: 'transparent' }}></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="grand-total-row">
              <td colSpan={5} className="grand-total-label">
                <strong>ОБЩИЙ ИТОГ:</strong>
              </td>
              <td className="numeric grand-total">
                <strong>{formatCurrency(totalRevenue)}</strong>
              </td>
              <td className="numeric grand-total">
                <strong>{formatCurrency(totalPaid)}</strong>
              </td>
              <td className="numeric grand-total">
                <strong>{formatCurrency(totalUnpaid)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </section>

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableOrderReport;
